class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

/**
 * ✅ Cart page required agreements:
 * - disables #checkout until both checkboxes are checked
 * - blocks additional checkout buttons (Shop Pay, GPay, etc.) until checked
 * - safe to call multiple times (re-binds after AJAX renders)
 */
function initCartPageAgreements() {
  const agreementsWrap = document.getElementById('CartPage-Agreements');
  const checkoutBtn = document.getElementById('checkout');
  const privacyCb = document.getElementById('CartPage-Agreement-Privacy');
  const termsCb = document.getElementById('CartPage-Agreement-Terms');
  const hint = document.getElementById('CartPage-AgreementHint');

  // If we are not on cart page footer (or cart empty / section not present), skip
  if (!agreementsWrap || !checkoutBtn || !privacyCb || !termsCb) return;

  const dynamicWrap =
    document.querySelector('#main-cart-footer .cart__dynamic-checkout-buttons') ||
    document.querySelector('#main-cart-footer .additional-checkout-buttons');

  const setDynamicDisabled = (disabled) => {
    if (!dynamicWrap) return;

    if (disabled) {
      dynamicWrap.style.pointerEvents = 'none';
      dynamicWrap.style.opacity = '0.55';
      dynamicWrap.setAttribute('aria-disabled', 'true');
    } else {
      dynamicWrap.style.pointerEvents = '';
      dynamicWrap.style.opacity = '';
      dynamicWrap.removeAttribute('aria-disabled');
    }
  };

  const updateState = () => {
    const ok = privacyCb.checked && termsCb.checked;

    // Keep empty cart behavior too
    const cartFooter = document.getElementById('main-cart-footer');
    const isEmpty = cartFooter && cartFooter.classList.contains('is-empty');

    if (isEmpty) {
      checkoutBtn.setAttribute('disabled', 'disabled');
      setDynamicDisabled(true);
      if (hint) hint.hidden = true;
      return;
    }

    if (ok) {
      checkoutBtn.removeAttribute('disabled');
      setDynamicDisabled(false);
      if (hint) hint.hidden = true;
    } else {
      checkoutBtn.setAttribute('disabled', 'disabled');
      setDynamicDisabled(true);

      // ✅ SHOW red hint text whenever agreements are not complete
      if (hint) hint.hidden = false;
    }
  };

  // Prevent multiple duplicate listeners after re-render:
  if (!agreementsWrap.dataset.agreementsBound) {
    agreementsWrap.dataset.agreementsBound = 'true';

    privacyCb.addEventListener('change', updateState);
    termsCb.addEventListener('change', updateState);

    // Extra safety if something still becomes clickable
    const showHintIfNeeded = (e) => {
      const ok = privacyCb.checked && termsCb.checked;
      if (!ok) {
        if (hint) hint.hidden = false;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    checkoutBtn.addEventListener('click', showHintIfNeeded);
    checkoutBtn.addEventListener('mousedown', showHintIfNeeded);
    checkoutBtn.addEventListener('pointerdown', showHintIfNeeded);
    checkoutBtn.addEventListener('touchstart', showHintIfNeeded, { passive: false });

    if (dynamicWrap) {
      dynamicWrap.addEventListener(
        'click',
        (e) => {
          const ok = privacyCb.checked && termsCb.checked;
          if (!ok) {
            if (hint) hint.hidden = false;
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
      dynamicWrap.addEventListener(
        'pointerdown',
        (e) => {
          const ok = privacyCb.checked && termsCb.checked;
          if (!ok) {
            if (hint) hint.hidden = false;
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    }
  }

  updateState();
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    const cartItemsSum = document.querySelectorAll('.drawer__cart-items-wrapper .quantity__input[type="number"]');
    let totalcartItemsSum = 0;
    cartItemsSum.forEach((input) => (totalcartItemsSum += Number(input.value)));
    const updateCartQuantityIcons = document.querySelectorAll('.totals__items');
    if (updateCartQuantityIcons.length) {
      updateCartQuantityIcons.forEach((icon) => {
        icon.textContent = totalcartItemsSum;
      });
    }

    this.initSlider();

    // ✅ Init cart page agreements on first load
    setTimeout(() => initCartPageAgreements(), 0);
  }

  initSlider() {
    const getSliders = document.querySelectorAll('.recommendations-slider-container');
  
    getSliders.forEach((itemSlider) => {
      const id = itemSlider.dataset.sectionId;
      const sliderId = itemSlider.classList.contains(`recommendations-slider-container-${id}`);
  
      if (sliderId) {
        const slider = itemSlider.querySelector('.recommendations-slider');
        const prevBtn = itemSlider.querySelector('.prev');
        const nextBtn = itemSlider.querySelector('.next');
  
        prevBtn.tabIndex = 0;
        nextBtn.tabIndex = 0;
  
        let currentIndex = 0;
        const slideWidth = itemSlider.querySelector('.recommendations-slide').clientWidth;
  
        function updateSlider() {
          const translateXValue = -currentIndex * slideWidth + 'px';
          slider.style.transform = 'translateX(' + translateXValue + ')';
          prevBtn.disabled = currentIndex === 0;
          nextBtn.disabled = currentIndex === slider.children.length - 1;
        }
  
        function handleKeyboardEvent(event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.click();
          }
        }
  
        function goToPrev() {
          if (currentIndex > 0) {
            currentIndex--;
            updateSlider();
          }
        }
  
        function goToNext() {
          if (currentIndex < slider.children.length - 1) {
            currentIndex++;
            updateSlider();
          }
        }
  
        prevBtn.addEventListener('click', goToPrev);
        nextBtn.addEventListener('click', goToNext);
        prevBtn.addEventListener('keydown', handleKeyboardEvent);
        nextBtn.addEventListener('keydown', handleKeyboardEvent);
  
        updateSlider();
      }
    });
  }
  
  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);

    this.initSlider();
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }

    this.initSlider();

    // ✅ Re-init cart page agreements after updates
    setTimeout(() => initCartPageAgreements(), 0);
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line, quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });


    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          let getSectionId = document.getElementById(section.id);
          if (getSectionId != null) {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector,
            );
          }
        });

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        const updateCartQuantityIconQ = document.querySelectorAll('.totals__items');
        if (updateCartQuantityIconQ.length) {
          updateCartQuantityIconQ.forEach((icon) => {
            icon.textContent = parsedState.item_count;
          });
        }

        // ✅ Re-init agreements because main-cart-footer gets re-rendered here
        setTimeout(() => initCartPageAgreements(), 0);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}

// ✅ Also try init on DOM ready (extra safe)
document.addEventListener('DOMContentLoaded', () => {
  initCartPageAgreements();
});

